/**
 * MoveDialog — collects a destination FOLDER path then moves N items into
 * it. Reuses each item's basename so files keep their names.
 *
 * v1: text input (no folder tree picker). Folder tree picker is a P2 follow-up
 * — a text input is fast, accessible, and doesn't require a separate API to
 * enumerate folders. The current path is preselected for orientation.
 *
 * Validation: empty target = root (allowed). Leading slash trimmed.
 * Self-move (target == current parent) is no-op.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface MoveDialogProps {
  open: boolean;
  /** Source paths being moved. Length 1 for single item, N for bulk. */
  sources: string[];
  /** Pre-fill the input with this folder (e.g., the current folder). */
  initialTarget?: string;
  busy?: boolean;
  onCancel: () => void;
  /**
   * Called with `targetFolder` (no trailing slash) when admin confirms.
   * Caller is responsible for building per-source `to` paths and calling
   * the bulk-move endpoint.
   */
  onConfirm: (targetFolder: string) => void;
}

function normalizeFolder(input: string): string {
  return String(input || '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .replace(/\/{2,}/g, '/');
}

export function MoveDialog({
  open, sources, initialTarget, busy, onCancel, onConfirm,
}: MoveDialogProps) {
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setTarget(initialTarget || '');
      setError('');
    }
  }, [open, initialTarget]);

  const handleConfirm = () => {
    const folder = normalizeFolder(target);
    // Detect self-move: every source already in this folder
    const allInTarget = sources.every((src) => {
      const idx = src.lastIndexOf('/');
      const parent = idx >= 0 ? src.slice(0, idx) : '';
      return parent === folder;
    });
    if (allInTarget) {
      setError('All selected items are already in that folder.');
      return;
    }
    onConfirm(folder);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Move {sources.length === 1 ? '1 item' : `${sources.length} items`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Type the destination folder path. Leave empty to move to the root.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Destination folder"
            placeholder="e.g., archive/2026"
            value={target}
            onChange={(e) => { setTarget(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
            disabled={busy}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
          />
          {sources.length > 1 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Each item keeps its filename inside the new folder.
            </Typography>
          )}
          {sources.length <= 5 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Moving:</Typography>
              {sources.map((src) => (
                <Typography key={src} variant="caption" sx={{ fontFamily: 'monospace' }} noWrap>
                  /{src}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={busy} variant="contained">
          {busy ? 'Moving...' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
